import { Box } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import React from 'react';
import { primitiveColors, semanticColors } from '@/theme/tokens';
import { BallotDotState } from '@/types/ballotRecords';

interface BallotStatusDotProps {
  state: BallotDotState;
  size?: number;
}

/**
 * Status indicator for ballot records:
 * green dot = saved + every race picked (complete), blue dot = saved,
 * light-orange dot = draft; past elections use a check — green = complete,
 * blue = saved; gray dot = draft + past.
 *
 * Purely decorative — pair it with a visible text label (Draft/Saved/Complete)
 * so color is never the only signal.
 */
const BallotStatusDot: React.FC<BallotStatusDotProps> = ({ state, size = 8 }) => {
  if (state === 'savedPast' || state === 'completePast') {
    const checkColor = state === 'completePast' ? primitiveColors.green3 : primitiveColors.blue2;
    // Center the check in the same `size`-wide box as the dots so both align in
    // the column; stroke thickens the glyph so the color reads more clearly.
    return (
      <Box
        aria-hidden="true"
        sx={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <CheckIcon
          sx={{ fontSize: size + 6, color: checkColor, stroke: checkColor, strokeWidth: 1 }}
        />
      </Box>
    );
  }
  const color =
    state === 'complete'
      ? primitiveColors.green3 // green — saved + every race picked
      : state === 'savedUpcoming'
        ? primitiveColors.blue2 // blue — saved
        : state === 'draftUpcoming'
          ? primitiveColors.orange1 // light orange — draft
          : semanticColors.textTertiary; // gray — draft + past
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
  );
};

export default BallotStatusDot;
